import { Box, Typography, useMediaQuery, Tabs, Tab, Divider, Grid, TextField, useTheme, Button } from '@mui/material';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ToolsDetails, Participants, Frequenters } from '../../../../../../components/custom';
import { CustomAlert } from '../../../../../../components/shared';
import { LayoutBase } from '../../../../../../layouts';
import { EmegencySimulationsServices, IEmergencySimulation } from '../../../../../../services/api/action-plan/EmergencySimulation';
import { IParticipantes } from '../../../../../../services/api/action-plan/InternalTraining';
import { IFrequentadores } from '../../../../../../services/api/action-plan/Seminario';
import { useAuthContext } from '../../../../../../context';
import { NavigateBefore, NavigateNext, Save } from '@mui/icons-material';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography component='div'>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

function tabProps(index: number) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

export const EmergencySituationSimulationDetails = () => {

    const [alertInfo, setAlertInfo] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({
        open: false,
        message: '',
        severity: 'success'
    });

    const [value, setValue] = React.useState(0);
    const handleChangeTab = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };
    const { userData } = useAuthContext();
    const url = useParams();
    const theme = useTheme();
    const smDown = useMediaQuery(theme.breakpoints.down('sm'));
    const [valid, setValid] = useState<boolean>(false);
    const navigate = useNavigate();
    const location = useLocation();

    const [data, setData] = useState<IEmergencySimulation>({
        data_simulado: '',
        instituicoes_participantes: '',
        periodo_referencia: '',
        participantes: [],
        frequentadores: [],
    });

    const pagePermissionsString = useMemo(() => {
        return userData?.nivel_acesso?.PAE?.simulado;
    }, [userData]);

    const hasActionPermission = useCallback((action: 'add' | 'edit' | 'remove' | 'view'): boolean => {
        if (typeof pagePermissionsString === 'string' && pagePermissionsString !== '') {
            const actions = pagePermissionsString.split(',').map(s => s.trim());
            return actions.includes(action);
        }
        return false;
    }, [pagePermissionsString]);

    useEffect(() => {
        setData({
            data_simulado: '',
            instituicoes_participantes: '',
            periodo_referencia: '',
            participantes: [],
            frequentadores: [],
        });
        fetchData();
    }, [location.pathname]);

    function verifyForm() {
        return !!data.data_simulado && !!data.periodo_referencia;
    }

    const handleNew = () => {
        if (!hasActionPermission('add')) {
            setAlertInfo({ open: true, message: 'Você não tem permissão para adicionar um novo simulado.', severity: 'error' });
            return;
        }
        navigate(`/information_dam/${url.id}/emergency_situation_simulation/detalhes/novo`);
    };

    const fetchData = () => {
        if (url.idEmergecy) {
            EmegencySimulationsServices.get(url.idEmergecy)
                .then((response) => {
                    if (response instanceof Error) {
                        console.log(response.message);
                    } else {
                        response.data_simulado = response.data_simulado ? new Date(response.data_simulado).toISOString().substring(0, 10) : '';
                        response.periodo_referencia = response.periodo_referencia ? new Date(response.periodo_referencia).toISOString().substring(0, 10) : '';
                        setData(response);
                    }
                });
        }
    };

    const handleParticipants = (value: IParticipantes[]) => {
        setData(prevData => { if (prevData) { return { ...prevData, participantes: value }; } return { participantes: value } as IEmergencySimulation; });
    };

    const handleFrequenters = (value: IFrequentadores[]) => {
        setData(prevData => { if (prevData) { return { ...prevData, frequentadores: value }; } return { frequentadores: value } as IEmergencySimulation; });
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        const newData = {
            ...data,
            [name]: value,
        };
        setData(newData);
        setValid(false);
    };

    const handleSave = () => {
        const isEditing = url.idEmergecy !== 'novo';
        const hasAddPermission = hasActionPermission('add');
        const hasEditPermission = hasActionPermission('edit');
        if ((!isEditing && !hasAddPermission) || (isEditing && !hasEditPermission)) {
            setAlertInfo({ open: true, message: 'Você não tem permissão para realizar esta ação.', severity: 'error' });
            return;
        }

        if (url.idEmergecy == 'novo') {
            if (verifyForm()) {
                if (url.id) {
                    data.id_barragem = url.id;
                    EmegencySimulationsServices.create(data)
                        .then((response) => {
                            if (response instanceof Error) {
                                setAlertInfo({ open: true, message: `Error: ${response.message}`, severity: 'error' });
                            } else {
                                setAlertInfo({ open: true, message: 'Simulado Criado', severity: 'success' });
                                setTimeout(() => {
                                    navigate(`/information_dam/${url.id}/emergency_situation_simulation`);
                                }, 2000);
                            }
                        });
                }
            } else {
                setValid(true);
            }
        } else {
            if (url.idEmergecy !== undefined) {
                delete data.uuid;
                EmegencySimulationsServices.upgrade(data, url.idEmergecy)
                    .then((response) => {
                        if (response instanceof Error) {
                            setAlertInfo({ open: true, message: `Error: ${response.message}`, severity: 'error' });
                        } else {
                            setAlertInfo({ open: true, message: 'Simulado atualizado', severity: 'success' });
                            setTimeout(() => {
                                navigate(`/information_dam/${url.id}/emergency_situation_simulation`);
                            }, 2000);
                        }
                    });
            }
        }
    };

    const handleDelete = () => {
        if (!hasActionPermission('remove')) {
            setAlertInfo({ open: true, message: 'Você não tem permissão para deletar este simulado.', severity: 'error' });
            return;
        }
        if (url.idAco !== undefined && url.idAco !== 'novo') {
            EmegencySimulationsServices.delet(url.idAco)
                .then((response) => {
                    if (response instanceof Error) {
                        console.log(response.message);
                        setAlertInfo({ open: true, message: `Error: ${response.message}`, severity: 'error' });

                    } else {
                        setAlertInfo({ open: true, message: 'ACO excluido', severity: 'success' });
                    }
                });
        }
    };

    const handleChangeButtonNext = () => {
        if (value < 2) {
            setValue(value + 1);
        }
    };
    const handleChangeButtonBefor = () => {
        if (value > 0) {
            setValue(value - 1);
        }
    };

    return (
        <LayoutBase
            title='Cadastro Situação de Emergência'
            tollBar={<ToolsDetails
                showDeletButton={!!userData.permissoes.includes('remove')}
                showSaveButton={!!userData.permissoes.includes('edit')}
                showNewButton={!!userData.permissoes.includes('add')}
                onClickSave={() => handleSave()}
                onClickNew={() => handleNew()}
                onClickDelet={() => handleDelete()}
            />}
        >
            <Box padding={1}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }} justifyContent={'space-around'} display={'flex'}>
                    <Tabs value={value} onChange={handleChangeTab} variant="scrollable" scrollButtons allowScrollButtonsMobile>
                        <Tab sx={{ color: valid ? '#d95b40' : theme.palette.primary.main, '&.Mui-disabled': { color: valid ? '#d95b40' : theme.palette.primary.main } }} label="Informações" {...tabProps(0)} disabled />
                        <Tab sx={{ color: theme.palette.primary.main, '&.Mui-disabled': { color: theme.palette.primary.main } }} label="Participantes" {...tabProps(1)} disabled />
                        <Tab sx={{ color: theme.palette.primary.main, '&.Mui-disabled': { color: theme.palette.primary.main } }} label="Frequentadores/Moradores" {...tabProps(2)} disabled />
                    </Tabs>
                </Box>
                <Box padding={1} display={'flex'} justifyContent={'space-between'} flexDirection={smDown ? 'column' : 'row'} gap={smDown ? '5px' : 0}>
                    <Button
                        variant='contained'
                        sx={{ borderRadius: '25px' }}
                        startIcon={<NavigateBefore />}
                        disabled={value == 0}
                        onClick={() => handleChangeButtonBefor()}
                    >Voltar</Button>
                    <Button
                        variant='contained'
                        sx={{ borderRadius: '25px' }}
                        startIcon={value != 2 ? <NavigateNext /> : <Save />}
                        onClick={value != 2 ? () => handleChangeButtonNext() : () => handleSave()}
                        disabled={
                            (url.idEmergecy == 'novo' && !hasActionPermission('add')) ||
                            (url.idEmergecy !== 'novo' && !hasActionPermission('edit'))
                        }
                    >{value != 2 ? 'Proximo' : 'Salvar'}</Button>

                </Box>
                <CustomTabPanel value={value} index={0}>
                    <Box>
                        <Box
                            sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'start' }}
                            padding={1}
                        >
                            <Typography><strong>Informações simulador de emergência</strong></Typography>
                        </Box>
                        <Divider />
                        <Grid container padding={2} spacing={2}>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Ano/Período de referência '
                                    type='date'
                                    name='periodo_referencia'
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.periodo_referencia}
                                    error={valid ? (data.periodo_referencia == '' ? true : false) : false}
                                    required
                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Data do simulado'
                                    type='date'
                                    name='data_simulado'
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    onChange={(e) => handleChange(e)}
                                    value={data?.data_simulado}
                                    error={valid ? (data.data_simulado == '' ? true : false) : false}
                                    required
                                />
                            </Grid>
                            <Grid item xs={smDown ? 12 : 6}>
                                <TextField
                                    label='Instituições participantes '
                                    type='text'
                                    name='instituicoes_participantes'
                                    fullWidth
                                    onChange={(e) => handleChange(e)}
                                    value={data?.instituicoes_participantes}
                                />
                            </Grid>

                        </Grid>
                    </Box>
                </CustomTabPanel>

                <CustomTabPanel value={value} index={1}>
                    <Participants value={data.participantes} onChange={handleParticipants} smDown={smDown} />
                </CustomTabPanel>

                <CustomTabPanel value={value} index={2}>
                    <Frequenters value={data.frequentadores} onChange={handleFrequenters} smDown={smDown} />
                </CustomTabPanel>
            </Box>
            {alertInfo.open &&
                <CustomAlert
                    severity={alertInfo.severity}
                    message={alertInfo.message}
                />
            }
        </LayoutBase>
    );
};